/**
 * Per-language runtime-version and compiler-flag preferences — `PG-IDE01`.
 *
 * **Everything here is a preference, never a permission.** The backend validates
 * the version id and every flag token on the run boundary; this only remembers
 * which of the *allowed* values a student picked last, so switching to C++ does
 * not silently reset them to the default every time.
 *
 * The read path is deliberately forgiving and *loud*: a stored value the
 * contract no longer declares resolves to the default and reports what it
 * dropped, so the UI can say so. A silent reset would be the same class of bug
 * as a picker that cannot express "C++17 only".
 */

import {
  isCompilerOptionAllowed,
  isLanguageVersionSelectable,
  resolvePlaygroundVersion,
  type PlaygroundEngineId,
} from "../config/backend-language-contract.generated";

const VERSION_KEY_PREFIX = "progsity:playground:version:";
const OPTIONS_KEY_PREFIX = "progsity:playground:cflags:";
/** A stored list longer than the whole allowlist is corrupt, not a preference. */
const MAX_STORED_OPTIONS = 12;

function versionKey(languageId: string): string {
  return `${VERSION_KEY_PREFIX}${languageId}`;
}

function optionsKey(languageId: string): string {
  return `${OPTIONS_KEY_PREFIX}${languageId}`;
}

export type StoredVersionSelection = {
  /** Always a currently-selectable id on the engine that was asked about. */
  versionId: string;
  /** The stored id that had to be dropped, or `null`. */
  fellBackFrom: string | null;
  /**
   * The stored id that was *superseded*, or `null`.
   *
   * A preference set before a cutover names a version the new engine does not
   * carry, and that is an interpreter bump rather than a lost pin: the same
   * distinction the run response makes, kept here so a reload after a flip says
   * "Python 3.8 has been retired" instead of "Python 3.8 is no longer
   * available".
   */
  supersededFrom: string | null;
};

/**
 * The remembered version for a language, resolved against the live contract.
 *
 * Returns the language's default when nothing is stored — which is not a
 * fallback and is reported as `fellBackFrom: null`, because "never chose one"
 * and "chose one that is gone" are different things to tell a student.
 */
export function loadLanguageVersion(
  languageId: string,
  engine: PlaygroundEngineId,
): StoredVersionSelection | null {
  const resolveDefault = (stored: string | null): StoredVersionSelection | null => {
    const resolved = resolvePlaygroundVersion(languageId, stored, engine);
    return resolved
      ? {
          versionId: resolved.version.id,
          fellBackFrom: resolved.fellBackFrom,
          supersededFrom: resolved.supersededFrom,
        }
      : null;
  };

  if (typeof window === "undefined") return resolveDefault(null);
  try {
    return resolveDefault(window.localStorage.getItem(versionKey(languageId)));
  } catch {
    return resolveDefault(null);
  }
}

export function saveLanguageVersion(
  languageId: string,
  versionId: string,
  engine: PlaygroundEngineId,
): void {
  if (typeof window === "undefined") return;
  try {
    // Refuse to persist something the server would reject: a stale preference
    // that survives a contract change is a fallback notice on every reload.
    if (!isLanguageVersionSelectable(languageId, versionId, engine)) {
      window.localStorage.removeItem(versionKey(languageId));
      return;
    }
    window.localStorage.setItem(versionKey(languageId), versionId);
  } catch {
    /* quota / private mode */
  }
}

/** Remembered flags, filtered to the ones the backend still allows. */
export function loadCompilerOptions(
  languageId: string,
  engine: PlaygroundEngineId,
): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(optionsKey(languageId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((token): token is string => typeof token === "string")
      .filter((token) => isCompilerOptionAllowed(languageId, token, engine))
      .slice(0, MAX_STORED_OPTIONS);
  } catch {
    return [];
  }
}

export function saveCompilerOptions(
  languageId: string,
  options: readonly string[],
  engine: PlaygroundEngineId,
): void {
  if (typeof window === "undefined") return;
  try {
    const allowed = options
      .filter((token) => isCompilerOptionAllowed(languageId, token, engine))
      .slice(0, MAX_STORED_OPTIONS);
    if (allowed.length === 0) {
      window.localStorage.removeItem(optionsKey(languageId));
      return;
    }
    window.localStorage.setItem(optionsKey(languageId), JSON.stringify(allowed));
  } catch {
    /* quota / private mode */
  }
}
